/**
 * YunShl Behavior Tracker SDK
 * 行为追踪SDK - 用于商城前端埋点
 *
 * 功能：
 * 1. 商品浏览追踪
 * 2. 搜索热词追踪
 * 3. 加购行为追踪
 * 4. 页面停留时长计算
 * 5. 批量上报功能
 */

import StorageUtil from '../utils/storage-util';
import {SystemConst} from '../utils/constant';
import AppUtil from '../utils/app-util';
import UrlUtil from '../utils/url-config';
import {CommonUtil} from '../utils/common-utils';
import md5 from 'md5';

const reqKey = "yunshang";
const API = UrlUtil.getAPI();

// 事件类型常量
export const BehaviorEventType = {
    GOODS_VIEW: 'goods_view',           // 商品浏览
    PAGE_VIEW: 'page_view',             // 页面浏览
    PAGE_STAY: 'page_stay',             // 页面停留
    SEARCH: 'search',                   // 搜索
    ADD_TO_CART: 'add_to_cart',         // 加购
    CART_MODIFY: 'cart_modify',         // 购物车修改
    CART_DELETE: 'cart_delete',         // 购物车删除
    ORDER_CREATE: 'order_create',       // 下单
    GOODS_FAVORITE: 'goods_favorite',   // 收藏
};

// 页面类型常量
export const PageType = {
    HOME: 1,            // 首页
    CATEGORY: 2,        // 分类页
    GOODS_DETAIL: 3,    // 商品详情
    SEARCH_RESULT: 4,   // 搜索结果
};

// 设备类型常量
export const DeviceType = {
    APP: 1,
    PC: 2,
    H5: 3,
    MINI_PROGRAM: 4,
};

/**
 * YunShlTracker - 行为追踪SDK
 */
const YunShlTracker = {
    // 配置
    config: {
        apiUrl: '',                    // 上报API地址
        batchSize: 10,                 // 批量上报数量阈值
        flushInterval: 5000,           // 上报间隔(ms)
        sessionTimeout: 30 * 60 * 1000, // 会话超时30分钟
        enabled: true,                 // 是否启用
        autoFlush: true,               // 是否自动上报（设为false可手动控制）
    },

    // 状态
    state: {
        sessionId: null,               // 会话ID
        deviceId: null,                // 设备ID
        userId: null,                  // 用户ID
        clientId: null,                // 客户ID
        companyId: null,               // 企业ID
        queue: [],                     // 待上报事件队列
        pageEnterTime: null,           // 页面进入时间
        currentPage: null,             // 当前页面信息
        flushTimer: null,              // 上报定时器
    },

    /**
     * 初始化SDK
     * @param {Object} options - 配置选项
     * @param {string} options.apiUrl - API地址
     * @param {number} options.batchSize - 批量上报数量
     * @param {number} options.flushInterval - 上报间隔
     * @param {boolean} options.enabled - 是否启用
     */
    init(options = {}) {
        this.config = {...this.config, ...options};
        this.config.apiUrl = this.config.apiUrl || `${API}/v1/shop/behavior`;

        // 生成/恢复会话ID
        this._initSession();

        // 生成/恢复设备ID
        this._initDeviceId();

        // 获取用户信息
        this._loadUserInfo();

        // 绑定页面事件
        this._bindPageEvents();

        // 启动定时上报
        this._startFlushTimer();

        console.log('[YunShlTracker] initialized', {
            sessionId: this.state.sessionId,
            deviceId: this.state.deviceId,
            enabled: this.config.enabled
        });
    },

    /**
     * 设置用户信息（登录后调用）
     * @param {number} userId - 用户ID
     * @param {number} clientId - 客户ID
     * @param {number} companyId - 企业ID
     */
    setUser(userId, clientId, companyId) {
        this.state.userId = userId;
        this.state.clientId = clientId;
        this.state.companyId = companyId;
    },

    /**
     * 追踪事件
     * @param {string} eventType - 事件类型
     * @param {Object} eventData - 事件数据
     */
    track(eventType, eventData = {}) {
        if (!this.config.enabled) return;

        // 动态获取最新的用户信息
        this._loadUserInfo();

        const event = {
            event_type: eventType,
            event_data: eventData,
            session_id: this.state.sessionId,
            device_id: this.state.deviceId,
            user_id: this.state.userId,
            client_id: this.state.clientId,
            company_id: this.state.companyId,
            device_type: this._getDeviceType(),
            timestamp: Date.now()
        };
        this.state.queue.push(event);

        // 达到批量数量立即上报
        if (this.state.queue.length >= this.config.batchSize) {
            this._flush();
        }
    },

    /**
     * 追踪商品浏览
     * @param {number} goodsId - 商品ID
     * @param {number} categoryId - 分类ID
     * @param {number} stayDuration - 停留时长(秒)
     */
    trackGoodsView(goodsId, categoryId, stayDuration = 0) {
        this.track(BehaviorEventType.GOODS_VIEW, {
            goods_id: goodsId,
            category_id: categoryId,
            page_type: PageType.GOODS_DETAIL,
            stay_duration: stayDuration
        });
    },

    /**
     * 追踪搜索
     * @param {string} keywords - 搜索关键词
     * @param {number} resultCount - 结果数量
     * @param {number} searchType - 搜索类型(1:关键词 2:VIN 3:车型)
     */
    trackSearch(keywords, resultCount, searchType = 1) {
        this.track(BehaviorEventType.SEARCH, {
            keywords: keywords,
            result_count: resultCount,
            search_type: searchType
        });
    },

    /**
     * 追踪搜索点击
     * @param {string} keywords - 搜索关键词
     * @param {number} searchType - 搜索类型(1:关键词 2:VIN 3:车型)
     */
    trackSearchClick(keywords, searchType = 1) {
        this.track(BehaviorEventType.SEARCH, {
            keywords: keywords,
            search_type: searchType,
            has_click: 1
        });
    },

    /**
     * 追踪加购
     * @param {number} goodsId - 商品ID
     * @param {number} productId - 规格ID
     * @param {number} number - 数量
     * @param {number} price - 价格
     */
    trackAddToCart(goodsId, productId, number, price) {
        this.track(BehaviorEventType.ADD_TO_CART, {
            goods_id: goodsId,
            product_id: productId,
            new_number: number,
            price: price,
            action_type: 1
        });
    },

    /**
     * 追踪购物车修改
     * @param {number} goodsId - 商品ID
     * @param {number} productId - 规格ID
     * @param {number} oldNumber - 原数量
     * @param {number} newNumber - 新数量
     * @param {number} price - 价格
     */
    trackCartModify(goodsId, productId, oldNumber, newNumber, price) {
        this.track(BehaviorEventType.CART_MODIFY, {
            goods_id: goodsId,
            product_id: productId,
            old_number: oldNumber,
            new_number: newNumber,
            price: price,
            action_type: 2
        });
    },

    /**
     * 追踪购物车删除
     * @param {number} goodsId - 商品ID
     * @param {number} productId - 规格ID
     * @param {number} number - 删除数量
     */
    trackCartDelete(goodsId, productId, number) {
        this.track(BehaviorEventType.CART_DELETE, {
            goods_id: goodsId,
            product_id: productId,
            number: number,
            action_type: 3
        });
    },

    /**
     * 追踪下单
     * @param {number} orderId - 订单ID
     * @param {number} amount - 订单金额
     */
    trackOrderCreate(orderId, amount) {
        this.track(BehaviorEventType.ORDER_CREATE, {
            order_id: orderId,
            amount: amount
        });
    },

    /**
     * 追踪收藏
     * @param {number} goodsId - 商品ID
     * @param {boolean} isFavorite - 是否收藏
     */
    trackGoodsFavorite(goodsId) {
        this.track(BehaviorEventType.GOODS_FAVORITE, {
            goods_id: goodsId,
        });
    },

    /**
     * 追踪页面浏览
     * @param {number} pageType - 页面类型
     * @param {string} pageUrl - 页面URL
     */
    trackPageView(pageType, pageUrl) {
        this.track(BehaviorEventType.PAGE_VIEW, {
            page_type: pageType,
            page_url: pageUrl || window.location.pathname,
            referrer: document.referrer
        });
    },

    /**
     * 追踪页面停留时长
     * @param {number} pageType - 页面类型
     * @param {number} stayDuration - 停留时长(秒)
     */
    trackPageStay(pageType, stayDuration) {
        this.track(BehaviorEventType.PAGE_STAY, {
            page_type: pageType,
            stay_duration: stayDuration
        });
    },

    /**
     * 手动上报
     */
    flush() {
        this._flush();
    },

    /**
     * 设置启用状态
     * @param {boolean} enabled - 是否启用
     */
    setEnabled(enabled) {
        this.config.enabled = enabled;
    },

    // ==================== 私有方法 ====================

    /**
     * 初始化会话
     */
    _initSession() {
        const sessionKey = '__ys_sid';
        const lastSessionTime = localStorage.getItem('__ys_sid_time');
        const now = Date.now();

        // 检查会话是否超时
        if (lastSessionTime && (now - parseInt(lastSessionTime)) < this.config.sessionTimeout) {
            this.state.sessionId = localStorage.getItem(sessionKey);
        }

        // 创建新会话
        if (!this.state.sessionId) {
            this.state.sessionId = this._generateId();
            localStorage.setItem(sessionKey, this.state.sessionId);
        }

        localStorage.setItem('__ys_sid_time', now.toString());
    },

    /**
     * 初始化设备ID
     */
    _initDeviceId() {
        const deviceKey = '__ys_did';
        this.state.deviceId = localStorage.getItem(deviceKey) || this._generateId();
        localStorage.setItem(deviceKey, this.state.deviceId);
    },

    /**
     * 加载用户信息
     */
    _loadUserInfo() {
        const ysUser = StorageUtil.getItem(SystemConst.mShop_user);
        if (ysUser) {
            this.state.userId = ysUser.id_;
            this.state.clientId = ysUser.client_id_;
            this.state.companyId = ysUser.company_id_;
        }
    },

    /**
     * 绑定页面事件
     */
    _bindPageEvents() {
        const self = this;

        // 页面可见性变化
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                self._recordPageStay();
            } else {
                self.state.pageEnterTime = Date.now();
            }
        });

        // 页面关闭前上报
        window.addEventListener('beforeunload', () => {
            self._recordPageStay();
            self._flush();
        });

        // 记录页面进入时间
        this.state.pageEnterTime = Date.now();
    },

    /**
     * 记录页面停留时长
     */
    _recordPageStay() {
        if (this.state.pageEnterTime && this.state.currentPage) {
            const duration = Math.floor((Date.now() - this.state.pageEnterTime) / 1000);
            if (duration > 0) {
                this.trackPageStay(this.state.currentPage.pageType, duration);
            }
        }
    },

    /**
     * 启动定时上报
     */
    _startFlushTimer() {
        // 如果禁用自动上报，不启动定时器
        if (!this.config.autoFlush) return;

        if (this.state.flushTimer) {
            clearInterval(this.state.flushTimer);
        }
        this.state.flushTimer = setInterval(() => {
            this._flush();
        }, this.config.flushInterval);
    },

    /**
     * 批量上报
     */
    _flush() {
        if (this.state.queue.length === 0) return;

        const events = this.state.queue.slice();
        this.state.queue = [];

        const data = {
            events: events,
            session_id: this.state.sessionId,
            device_id: this.state.deviceId,
            device_type: this._getDeviceType()
        };

        // 统一使用fetch上报，避免sendBeacon的CORS问题
        this._xhrFlush(data, events);
    },

    /**
     * XHR上报（降级方案）
     */
    _xhrFlush(data, events) {
        const token = localStorage.getItem('token');
        const timestamp = CommonUtil.getNonce();
        const nonce = CommonUtil.getRandomParam();

        const headers = {
            'Content-Type': 'application/json;charset=utf-8',
            'yunshltoken': token ? JSON.parse(token) : '',
            'timestamp': timestamp,
            'nonce': nonce,
            'tokentype': AppUtil.isApp ? 'APP' : (this._isMobile() ? 'WEBAPP' : 'PC'),
            'appsource': AppUtil.isIos ? 'IOS' : (AppUtil.isAndroid ? 'ANDROID' : (this._isMobile() ? 'WEBAPP' : 'PC')),
        };

        // 签名
        const checkKey = reqKey + headers.yunshltoken + timestamp + nonce + JSON.stringify(data);
        headers.signnatrue = md5(checkKey);

        fetch(`${this.config.apiUrl}/track/batch`, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(data)
        }).then(response => {
            if (response.ok) {
                console.log('[YunShlTracker] flush success, events:', events.length);
            } else {
                console.warn('[YunShlTracker] flush failed, status:', response.status);
                // HTTP错误时恢复队列
                this.state.queue = events.concat(this.state.queue);
            }
        }).catch(err => {
            console.warn('[YunShlTracker] flush error:', err);
            // 网络错误时恢复队列
            this.state.queue = events.concat(this.state.queue);
        });
    },

    /**
     * 生成UUID
     */
    _generateId() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    },

    /**
     * 获取设备类型
     */
    _getDeviceType() {
        const ua = navigator.userAgent;
        if (/miniProgram/i.test(ua)) return DeviceType.MINI_PROGRAM;
        if (AppUtil.isApp) return DeviceType.APP;
        if (this._isMobile()) return DeviceType.H5;
        return DeviceType.PC;
    },

    /**
     * 判断是否移动端
     */
    _isMobile() {
        return navigator.userAgent.match(/(iPhone|iPod|Android|ios)/i);
    },

    /**
     * 获取页面类型
     */
    _getPageType() {
        const path = window.location.pathname;
        if (/\/GoodsDetail/.test(path)) return PageType.GOODS_DETAIL;
        if (/\/GoodsSearchResult/.test(path)) return PageType.SEARCH_RESULT;
        if (/\/GoodsList|GoodsClassify/.test(path)) return PageType.CATEGORY;
        if (path === '/' || /\/ShopHome/.test(path)) return PageType.HOME;
        return 0;
    }
};

// 挂载到全局window对象，便于调试
if (typeof window !== 'undefined') {
    window.YunShlTracker = YunShlTracker;
}

export default YunShlTracker;



// WEBPACK FOOTER //
// ./src/utils/yunshl-tracker.js